import { useTranslation } from "next-i18next";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";
import { GetStaticProps } from "next";
import {
  INFILTRATION_STATIC_GAME_DATA_QUERY_KEY,
  ProgressView,
  StaticGameData,
  multicallStaticGameData,
} from "@looksrare/infiltration";
import { ChainId, chainInfo } from "@looksrare/config";
import { useCurrentChainInfo } from "@looksrare/utils";
import { createPublicClient, http } from "viem";
import { QueryClient, dehydrate } from "@tanstack/react-query";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { getSupportedChain } from "config/wagmi";
import { getCanonicalAndLanguageAlternates } from "config/seo";
import ErrorBoundary from "components/ErrorBoundary";

interface PageProps {
  staticGameData: StaticGameData;
}

const InfiltrationProgressPage: React.FC<PageProps> = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentChainInfo = useCurrentChainInfo();

  const title = "INFILTRATION - LooksRare";
  const description =
    "In LooksRare’s latest on-chain game, you’ll lead your squad on the heist of a lifetime. Enter the compound, bag as much ETH as you can, and try to survive.";
  const { canonical, languageAlternates } = getCanonicalAndLanguageAlternates(
    "/infiltration/progress",
    router,
    currentChainInfo.appUrl
  );

  return (
    <ErrorBoundary>
      <NextSeo
        title={title}
        description={description}
        openGraph={{
          url: canonical,
          title: t("{{pageTitle}} | LooksRare", { pageTitle: title }),
          description: description,
          images: [
            {
              url: `${currentChainInfo.appUrl}/images/infiltration/meta.png`,
              alt: title,
              width: 1600,
              height: 900,
              type: "image/png",
            },
          ],
        }}
        canonical={canonical}
        languageAlternates={languageAlternates}
      />
      <ProgressView />
    </ErrorBoundary>
  );
};

export default InfiltrationProgressPage;

export const getStaticProps: GetStaticProps = async ({ locale = "en" }) => {
  try {
    const queryClient = new QueryClient();
    const chain = getSupportedChain()[0];

    const alchemyRpcUrl = `${chainInfo[chain.id as ChainId].rpcUrl}${
      process.env.ALCHEMY_KEY ? `/${process.env.ALCHEMY_KEY}` : ""
    }`;
    const client = createPublicClient({
      chain,
      transport: http(alchemyRpcUrl),
    });

    const staticGameData = await multicallStaticGameData(chain.id, client);
    queryClient.setQueryData(INFILTRATION_STATIC_GAME_DATA_QUERY_KEY, staticGameData);

    return {
      props: {
        locale,
        staticGameData,
        dehydratedState: dehydrate(queryClient),
        ...(await serverSideTranslations(locale)),
      },
      /**
       * @note - The page's cache times are managed via `CDN-` & `Vercel-CDN-` cache-control headers in next.config.js
       */
      revalidate: 86400, // 24hr
    };
  } catch (e) {
    // @todo-infiltration page fetching - reinstate throwing on catch
    // throw new PageDataFetchError("/infiltration/progress", e);
    return {
      props: {
        ...(await serverSideTranslations(locale)),
        locale,
      },
    };
  }
};
